import { AI_ASSISTANT_KNOWLEDGE } from './ai-assistant.knowledge';
import { AiKnowledgeEntry } from './ai-assistant.types';

/** Từ nối tiếng Việt — bỏ khi so khớp token để câu hỏi viết khác nhau vẫn trùng ý. */
const STOP_WORDS = new Set([
    'la',
    'gi',
    'the',
    'nao',
    'sao',
    'lam',
    'dau',
    'co',
    'khong',
    'cua',
    'va',
    'voi',
    'thi',
    'bi',
    'cac',
    'mot',
    'de',
    'tren',
    'trong',
    'khi',
    'minh',
    'toi',
    'hay',
    'xin',
    'hoi',
    'vui',
    'long',
    'giup',
    'duoc',
    'can',
    'ra',
    'nay',
    'do',
    'a',
]);

/** Điểm tối thiểu để coi là khớp — dưới ngưỡng sẽ trả fallback thay vì đoán bừa. */
const MIN_SCORE = 6;

export function normalizeKnowledgeText(value: string): string {
    return (
        value
            .normalize('NFD')
            .replace(/\p{M}/gu, '')
            .toLowerCase()
            // NFD không tách đ/Đ — phải quy đổi thủ công để user gõ không dấu vẫn khớp.
            .replace(/đ/g, 'd')
            .replace(/[^\p{L}\p{N}\s]/gu, ' ')
            .replace(/\s+/g, ' ')
            .trim()
    );
}

function contentTokens(normalized: string): string[] {
    return normalized.split(' ').filter((t) => t.length > 1 && !STOP_WORDS.has(t));
}

/**
 * Tìm mục knowledge phù hợp nhất.
 */
export function matchKnowledge(question: string): { answer: string | null; entryId: string | null } {
    const normalized = normalizeKnowledgeText(question);
    if (!normalized) {
        return { answer: null, entryId: null };
    }

    const userTokens = new Set(contentTokens(normalized));
    if (!userTokens.size) {
        return { answer: null, entryId: null };
    }

    let best: { entry: AiKnowledgeEntry; score: number } | null = null;

    for (const entry of AI_ASSISTANT_KNOWLEDGE) {
        const score = scoreEntry(entry, normalized, userTokens);
        if (score >= MIN_SCORE && (!best || score > best.score)) {
            best = { entry, score };
        }
    }

    if (!best) {
        return { answer: null, entryId: null };
    }

    return { answer: best.entry.answer, entryId: best.entry.id };
}

/**
 * Tìm câu trả lời phù hợp nhất từ knowledge base.
 */
export function matchKnowledgeAnswer(question: string): string | null {
    return matchKnowledge(question).answer;
}

function scoreEntry(entry: AiKnowledgeEntry, normalized: string, userTokens: Set<string>): number {
    let score = 0;

    for (const q of entry.questions) {
        const nq = normalizeKnowledgeText(q);
        if (!nq) {
            continue;
        }

        if (nq === normalized) {
            return 1000 + (entry.priority ?? 0);
        }

        if (nq.length >= 8 && normalized.includes(nq)) {
            score = Math.max(score, 60);
        }

        const qTokens = contentTokens(nq);
        if (!qTokens.length) {
            continue;
        }

        const hit = qTokens.filter((t) => userTokens.has(t)).length;
        if (hit >= 2 || (hit === 1 && qTokens.length === 1)) {
            const ratio = hit / qTokens.length;
            score = Math.max(score, Math.round(ratio * 30) + hit * 4);
        }
    }

    for (const kw of entry.keywords) {
        const nkw = normalizeKnowledgeText(kw);
        if (nkw.length < 2) {
            continue;
        }

        const kwTokens = nkw.split(' ');
        if (kwTokens.length > 1) {
            if (normalized.includes(nkw)) {
                score += 5 + kwTokens.length * 3;
            }
        } else if (userTokens.has(nkw)) {
            score += 5;
        }
    }

    // Ưu tiên mục cụ thể hơn khi điểm ngang nhau, không đủ để tự vượt ngưỡng.
    return score > 0 ? score + (entry.priority ?? 0) / 10 : 0;
}
